import type { ReactElement } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { queryValue } from "../../app/navigation.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { FormUsagePanel } from "../../components/form-usage-panel.js";
import { StorageGauge } from "../../components/storage-gauge.js";

import { CancelSection } from "./tenant-cancel.js";
import { InvoiceExemptionSection } from "./tenant-invoice-exemption.js";
import { InvoiceSettingsSection, InvoicesSection } from "./tenant-invoices.js";
import type { CenterContract, CenterTenant } from "./tenant-types.js";
import { TenantUsers } from "./tenant-users.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** 見出しと値の 1 組（<c>dl</c> の中）。 */
function Item({ label, children }: { readonly label: string; readonly children: ReactElement | string | number }): ReactElement {
    return <div><dt className="text-xs text-slate-500">{label}</dt><dd>{children}</dd></div>;
}

function ContractSection({ tenant, contract }: {
    readonly tenant: CenterTenant;
    readonly contract: CenterContract;
}): ReactElement {
    return (
        <section className="card mb-4 p-6">
            <h2 className="section-title">契約の状況</h2>

            {contract.cancelled
                ? (
                    <div className="alert-warning mb-4">
                        <strong>解約済みです。</strong>行は残しています（課金の履歴から参照されるため）。
                    </div>
                )
                : null}

            <dl className="grid gap-3 sm:grid-cols-2">
                {/* 会員の画面の契約情報と同じ並び・呼び名。契約 ID はポータルの番号（URL の数字）、テナント ID はプリザンター側（/fs）の番号 */}
                <Item label="契約 ID"><span data-tenant-id="">{tenant.tenantId}</span></Item>
                <Item label="テナント ID">
                    <span data-service-tenant-id="">
                        {contract.serviceTenantId > 0
                            ? contract.serviceTenantId
                            : <span className="badge-warning">環境がまだありません</span>}
                    </span>
                </Item>
                {/* 作成日時はサーバが作った文言をそのまま出す。返さない古い応答では行ごと出さない */}
                {contract.createdTimeLabel === undefined || contract.createdTimeLabel === ""
                    ? null
                    : <Item label="作成日時"><span data-created-time="">{contract.createdTimeLabel}</span></Item>}
                <Item label="プラン">{contract.planLabel}</Item>
                <Item label="契約人数">{contract.qty}</Item>
                <Item label="登録ユーザ数">{contract.registeredUsersLabel}</Item>
                <Item label="追加ストレージ">{`${String(contract.storageSize)} GB`}</Item>
                {/* 使用量は会員の画面の契約情報と同じ部品・同じ文言（サーバが作る）。届かない（null）ときは行ごと出さない */}
                {contract.storageUsage === null
                    ? null
                    : <Item label="ストレージ使用量"><StorageGauge usage={contract.storageUsage} /></Item>}
                {/* 無償利用・月額以外の契約は、お支払方法と課金の開始日をサーバが返さない。文言だけを出す */}
                {contract.exemptLabel === undefined || contract.exemptLabel === null
                    ? (
                        <>
                            <Item label="お支払方法">{contract.paymentMethodLabel ?? "-"}</Item>
                            <Item label="課金の開始日">{contract.payDate ?? "-"}</Item>
                        </>
                    )
                    : <Item label="契約の種別"><span data-contract-exempt="">{contract.exemptLabel}</span></Item>}
            </dl>

            {/* フォームの受付件数も会員の画面と同じ部品。フォーム機能を付けている契約にだけサーバが付ける */}
            {contract.formUsage === null ? null : <div className="mt-4"><FormUsagePanel usage={contract.formUsage} /></div>}

            {/* アクティビティログは「送ったメールを見る」の横（2026-10-10 に利用者が決めた）。出す権限はそれぞれ別 */}
            {tenant.canSeeInvoices || tenant.canSeeActivity
                ? (
                    <div className="mt-4 flex flex-wrap gap-2" data-tenant-links="">
                        {tenant.canSeeInvoices
                            ? (
                                <>
                                    <Link className="btn-secondary btn-sm" to={adminCenterUrls.invoices(tenant.tenantId)}>請求書を見る</Link>
                                    <Link className="btn-secondary btn-sm" to={adminCenterUrls.mailsOfTenant(tenant.tenantId)}>
                                        送ったメールを見る
                                    </Link>
                                </>
                            )
                            : null}
                        {/* 課金の結果は送ったメールと同じ並び（その契約に絞る。請求の権限） */}
                        {tenant.canSeePayments
                            ? (
                                <Link className="btn-secondary btn-sm" to={adminCenterUrls.chargesOfTenant(tenant.tenantId)}>
                                    課金の結果を見る
                                </Link>
                            )
                            : null}
                        {tenant.canSeeActivity ? <ActivityLink tenantId={tenant.tenantId} /> : null}
                    </div>
                )
                : null}
        </section>
    );
}

/**
 * この契約のアクティビティログへの入口。運営の権限のどれかがあれば出す（アクティビティログを開ける人と同じ）。
 * 開いた先はこの契約に絞ってある。<b>置き場は契約の状況の「送ったメールを見る」の横。</b>
 * 契約の状況が出ない人（請求の権限が無い）には、見出しの下に単独で出す。
 */
function ActivityLink({ tenantId }: { readonly tenantId: number }): ReactElement {
    return <Link className="btn-secondary btn-sm" to={adminCenterUrls.activityOfTenant(tenantId)}>アクティビティログを見る</Link>;
}

/**
 * 契約 1 件の全体像。
 * <b>持っている欄しか出ない</b>——1 つも無いときは白紙にせず、その旨を出す。
 * 解約はいちばん下（<c>tenant-cancel.tsx</c>）。
 */
export function TenantPage(): ReactElement | null {
    const { search } = useLocation();
    const [query] = useSearchParams();
    // 今のアプリのリンクは /AdminCenter/Tenant/5、絞り込みのフォームは ?id=5
    const { id: pathId } = useParams();
    const data = useApiData<CenterTenant>(
        adminCenterApi.tenant(pathId ?? queryValue(query, "id") ?? "", search));
    usePageMeta(titleFor(data, data.state === "ready" ? data.value.title : "契約情報"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="契約情報" />;
    }
    const tenant = data.value;

    return (
        <div className="page max-w-5xl">
            <p className="mb-4"><Link to={adminCenterUrls.tenants} className="btn-secondary btn-sm">契約情報へ戻る</Link></p>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{tenant.title}</h1>
                    {/* 契約 ID は契約の状況の先頭に出すので、見出しの下には企業名だけ（2026-10-10 に利用者が決めた） */}
                    <p className="page-subtitle">{tenant.company}</p>
                </div>
            </div>

            {tenant.isEmpty
                ? (
                    <div className="empty-state text-sm text-slate-500">
                        この契約について、あなたの権限で見られる情報はありません。
                    </div>
                )
                : null}

            {tenant.contract === null ? null : <ContractSection tenant={tenant} contract={tenant.contract} />}
            {tenant.contract === null && tenant.canSeeActivity
                ? <p className="mb-4" data-tenant-links=""><ActivityLink tenantId={tenant.tenantId} /></p>
                : null}
            {tenant.invoiceSettings === null
                ? null
                : (
                    <InvoiceSettingsSection
                        tenantId={tenant.tenantId} title={tenant.title} settings={tenant.invoiceSettings}
                    />
                )}
            {/* 請求書を出さない印は請求の設定のすぐ下（付け外しで請求書が出る・出なくなる。請求の権限を持つ人だけにサーバが付ける） */}
            {tenant.invoiceExemption === null
                ? null
                : <InvoiceExemptionSection tenantId={tenant.tenantId} title={tenant.title} exemption={tenant.invoiceExemption} />}
            {tenant.canSeeInvoices ? <InvoicesSection tenantId={tenant.tenantId} title={tenant.title} /> : null}
            {tenant.canUnlock ? <TenantUsers tenant={tenant} /> : null}
            {/* 取り消せない操作は画面のいちばん下（請求の権限があり、解約していない契約だけ。サーバが決める） */}
            {tenant.cancellation === null
                ? null
                : <CancelSection tenantId={tenant.tenantId} title={tenant.title} cancellation={tenant.cancellation} />}
        </div>
    );
}
